import * as d3 from 'd3';

import { renderTickFormatXAxis } from './helpers';
import { TransferMetricEntry } from './interfaces';
import styles from './MiniTransferGraphD3.module.scss';

const TICKS_COUNT = 3;
const TIME_TRANSITION = 300;
const TICK_PADDING = 6;

interface Props {
  container: HTMLDivElement | null;
  transferMetrics: TransferMetricEntry[];
  width: number;
  height: number;
}

export class MiniTransferGraphD3 {
  container: HTMLDivElement | null;
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined> = d3.select<SVGSVGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
  groupMain: d3.Selection<SVGGElement, unknown, null, undefined>;

  xScaleBand: d3.ScaleBand<string> = d3.scaleBand();
  yScaleLinear: d3.ScaleLinear<number, number, never> | d3.ScaleSymLog<number, number, never> = d3.scaleLinear();

  axisBottom: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));
  axisLeft: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));

  transferMetrics: TransferMetricEntry[] = [];
  width: number;
  height: number;
  margin = { top: 18, right: 2, bottom: 3, left: 2 };

  // eslint-disable-next-line
  constructor(props: Props) {
    const { container, transferMetrics, width, height } = props;

    this.container = container;
    this.transferMetrics = transferMetrics;

    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    d3.select(container).select('svg').remove();

    this.svg = d3.select(container)
      .append('svg')
      .attr('id', 'svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom);

    this.groupMain = this.svg
      .append('g')
      .attr('id', 'content')
      .attr('transform', `translate( ${this.margin.left}, ${this.margin.top} )`);

    this.updateDataPoints();
  }

  updateDataPoints = () => {
    this.buildAxisX();
    this.buildAxisY();

    this.deleteDomainAxisX();

    this.drawXAxis();

    this.renderLines();
  };

  generateLines = () => {
    const line = d3.line<TransferMetricEntry>()
      .x(d => (this.xScaleBand(d.name) || 0) + (this.xScaleBand.bandwidth() / 2))
      .y(d => this.yScaleLinear(d.value))
      .curve(d3.curveCatmullRom);

    return { line };
  };

  renderLines = () => {
    const { transferMetrics, groupMain } = this;

    const { line } = this.generateLines();

    groupMain
      .append('path')
      .datum(transferMetrics)
      .attr('class', styles.shadowLine)
      .attr('d', line);

    groupMain
      .append('path')
      .datum(transferMetrics)
      .attr('class', styles.line)
      .attr('d', line);
  };

  deleteDomainAxisX () {
    const { container } = this;
    d3.select(container).select('#content').select('#axisX').selectAll('.domain').remove();
    d3.select(container).select('#content').select('#axisX').selectAll('.tick').selectAll('text').remove();
  }

  getFilteredTicks = () => {
    const domain = this.xScaleBand.domain();

    let filteredTicks: string[] = [];
    if (domain.length <= 4) {
      return domain;
    }
    
    filteredTicks = [
      domain[0],
      domain[Math.floor((domain.length) / 2)],
      domain[domain.length - 1]
    ];
    return filteredTicks;
  };

  updateAxis () {
    const { height } = this;

    this.buildAxisX();
    this.buildAxisY();

    const axisBottom = d3.axisBottom(this.xScaleBand)
      .tickValues(this.getFilteredTicks())
      .tickFormat((d) => renderTickFormatXAxis({ domainValue: d }))
      .tickSize(-height)
      .ticks(TICKS_COUNT)
      .tickPadding(TICK_PADDING);

    this.axisBottom
      .attr('fill', 'transparent')
      .attr('transform', `translate(0, ${this.height})`)
      .transition()
      .duration(TIME_TRANSITION)
      .call(axisBottom);

    this.deleteDomainAxisX();

    const axiLeft = d3.axisLeft(this.yScaleLinear)
      .tickFormat((d) => d3.format('')(d as number))
      .tickSize(0)
      .tickPadding(TICK_PADDING);

    this.axisLeft.transition()
      .duration(TIME_TRANSITION)
      .call(axiLeft);
  }

  buildAxisX () {
    const { width, transferMetrics } = this;

    this.xScaleBand = d3.scaleBand()
      .domain(transferMetrics.map(capsule => capsule.name))
      .range([0, width])
      .padding(0.1);
  }

  buildAxisY () {
    const { height } = this;
    const { maxY, minY } = this.getYDomainData();

    this.yScaleLinear = d3.scaleLinear()
      .domain([minY, maxY])
      .range([height, 0]);
  }

  getYDomainData = () => {
    const { transferMetrics } = this;

    const margin = 0.02;
    const dataY: number[] = transferMetrics.map((item) => item.value);

    const minY = Math.min(...dataY);
    const maxY = Math.max(...dataY);

    if (minY === 0) {
      return {
        minY,
        maxY: maxY + (maxY * margin),
      };
    }

    return {
      minY: minY - (minY * margin),
      maxY: maxY + (maxY * margin),
    };
  }

  drawXAxis () {
    const { height, groupMain } = this;

    const axisBottom = d3.axisBottom(this.xScaleBand)
      .tickValues(this.getFilteredTicks())
      .tickFormat((d) => renderTickFormatXAxis({ domainValue: d }))
      .tickSize(-height)
      .ticks(TICKS_COUNT)
      .tickPadding(TICK_PADDING);

    this.axisBottom = groupMain.append('g')
      .attr('id', 'axisX')
      .attr('class', styles.axisX)
      .attr('transform', `translate(0, ${this.height})`)
      .call(axisBottom);

    this.deleteDomainAxisX();
  }

  resize = (props: { width: number, height: number }) => {
    const { width, height } = props;
    const { container } = this;

    d3.select(container).select('#content').selectAll('*').remove();

    this.updateSize(width, height);
    this.updateAxis();
    this.updateDataPoints();
  };

  updateSize = (width: number, height: number) => {
    const { container } = this;

    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    const _width = this.width + this.margin.left + this.margin.right;
    const _height = this.height + this.margin.top + this.margin.bottom;

    d3.select(container).select('svg')
      .attr('width', _width)
      .attr('height', _height);
  };
}
